import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import logger from "@/utils/logger";
import { toast } from "sonner";
import adminApi from "./adminApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Image, Trash2 } from "lucide-react";

const MAX_UPLOAD_SIZE = 10 * 1024 * 1024; // 10 MB

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];

const EVENT_TYPE_OPTIONS = [
  { value: "wedding", label: "Wedding" },
  { value: "corporate", label: "Corporate" },
  { value: "traditional", label: "Traditional" },
  { value: "private", label: "Private" },
  { value: "other", label: "Other" },
];

const GalleryManagement = () => {
  const { t } = useTranslation();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: "", event_type: "wedding", description: "", is_featured: false,
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const fetchGallery = useCallback(async () => {
    try {
      const response = await adminApi.get(`${API}/gallery`);
      setGallery(response.data);
    } catch (error) {
      logger.error("Error fetching gallery:", error);
    } finally {
      setLoading(false);
    }
  }, [API, adminApi]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Please select a JPG, PNG, WebP, or GIF image.");
      return;
    }
    if (file.size > MAX_UPLOAD_SIZE) {
      toast.error("File too large. Max 10 MB.");
      return;
    }
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const resetUploadForm = () => {
    setShowUpload(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadForm({ title: "", event_type: "wedding", description: "", is_featured: false });
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadForm.title.trim()) {
      toast.error("Please select an image and enter a title.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      fd.append("title", uploadForm.title);
      fd.append("event_type", uploadForm.event_type);
      fd.append("description", uploadForm.description);
      fd.append("is_featured", uploadForm.is_featured);
      await adminApi.post(`${API}/gallery/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded successfully!");
      resetUploadForm();
      fetchGallery();
    } catch (error) {
      const msg = error.response?.data?.detail || "Upload failed. Please try again.";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm(t("admin.dashboard.confirmDelete.gallery"))) return;
    try {
      await adminApi.delete(`${API}/gallery/${itemId}`);
      toast.success(t("admin.dashboard.toast.galleryDeleted"));
      fetchGallery();
    } catch {
      toast.error(t("admin.dashboard.toast.galleryDeleteError"));
    }
  };

  const renderGalleryGrid = () => {
    if (loading) {
      return <div className="flex justify-center py-12"><div className="spinner"></div></div>;
    }
    if (gallery.length === 0) {
      return (
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Image className="w-12 h-12 text-[#6B705C] mx-auto mb-4" />
            <p className="text-[#6B705C]">No photos yet. Upload your first photo!</p>
          </CardContent>
        </Card>
      );
    }
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {gallery.map((item) => (
          <div key={item.id} className="relative group rounded-xl overflow-hidden" data-testid={`admin-gallery-${item.id}`}>
            <img src={item.image_url} alt={item.title} className="w-full h-48 object-cover" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <Button variant="destructive" size="sm" onClick={() => deleteItem(item.id)} data-testid={`delete-gallery-${item.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
              <p className="text-white text-sm font-medium truncate">{item.title}</p>
            </div>
            {item.is_featured && (
              <div className="absolute top-2 right-2">
                <Badge className="bg-[#F2CC8F] text-[#3D405B] text-xs">Featured</Badge>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div data-testid="gallery-management" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#3D405B] font-['Playfair_Display']">{t("admin.dashboard.gallery")}</h1>
          <p className="text-[#6B705C]">{t("admin.dashboard.manageGallery")}</p>
        </div>
        <Button onClick={() => setShowUpload(!showUpload)} data-testid="upload-photo-btn" className="bg-[#2D6A4F] hover:bg-[#245a42] text-white">
          <Image className="w-4 h-4 mr-2" />
          {showUpload ? "Cancel" : "Upload Photo"}
        </Button>
      </div>

      {showUpload && (
        <Card className="bg-white border-0 shadow-sm" data-testid="gallery-upload-form">
          <CardContent className="p-6 space-y-4">
            <h3 className="font-semibold text-[#3D405B] text-lg">Upload New Photo</h3>
            <div
              className="border-2 border-dashed border-[#6B705C]/30 rounded-xl p-6 text-center cursor-pointer hover:border-[#2D6A4F] transition-colors"
              onClick={() => document.getElementById("gallery-file-input").click()}
              data-testid="file-drop-zone"
            >
              {previewUrl ? (
                <div className="space-y-3">
                  <img src={previewUrl} alt="Preview" className="max-h-48 mx-auto rounded-lg object-cover" data-testid="upload-preview" />
                  <p className="text-sm text-[#6B705C]">{selectedFile?.name}</p>
                  <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedFile(null); setPreviewUrl(null); }} data-testid="remove-file-btn">Remove</Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Image className="w-10 h-10 mx-auto text-[#6B705C]/50" />
                  <p className="text-[#6B705C] font-medium">Click to select a photo</p>
                  <p className="text-sm text-[#6B705C]/70">JPG, PNG, WebP or GIF — Max 10 MB</p>
                </div>
              )}
              <input id="gallery-file-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleFileSelect} data-testid="gallery-file-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#3D405B] mb-1 block">Title *</label>
              <input type="text" value={uploadForm.title} onChange={(e) => setUploadForm((p) => ({ ...p, title: e.target.value }))} placeholder="e.g. Elegant Wedding Reception" className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/30" data-testid="upload-title-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#3D405B] mb-1 block">Event Type</label>
              <Select value={uploadForm.event_type} onValueChange={(v) => setUploadForm((p) => ({ ...p, event_type: v }))}>
                <SelectTrigger className="w-full" data-testid="upload-event-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#3D405B] mb-1 block">Description</label>
              <textarea value={uploadForm.description} onChange={(e) => setUploadForm((p) => ({ ...p, description: e.target.value }))} placeholder="Optional description..." rows={2} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2D6A4F]/30 resize-none" data-testid="upload-description-input" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="upload-featured-toggle">
              <input type="checkbox" checked={uploadForm.is_featured} onChange={(e) => setUploadForm((p) => ({ ...p, is_featured: e.target.checked }))} className="rounded border-gray-300" />
              <span className="text-sm text-[#3D405B]">Mark as featured</span>
            </label>
            <Button onClick={handleUpload} disabled={uploading || !selectedFile || !uploadForm.title.trim()} data-testid="confirm-upload-btn" className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white py-5 rounded-full">
              {uploading ? <span className="flex items-center gap-2"><div className="spinner w-4 h-4" /> Uploading...</span> : "Upload Photo"}
            </Button>
          </CardContent>
        </Card>
      )}

      {renderGalleryGrid()}
    </div>
  );
};

export default GalleryManagement;
